import { useCallback, useEffect, useRef, useState } from 'react';
import { useIsFetching } from '@tanstack/react-query';
import * as Progress from '@radix-ui/react-progress';
import { useGlobalTransition } from 'components/router/useGlobalTransition';
import { useNonBlockingLocation } from 'components/router/useNonBlockingLocation';
import { TestIds } from 'constants/tests';
import useDebounce from 'customHooks/useDebounce';
import { twMerge } from 'tailwind-merge';

const MAX_COUNT = 80;
const TURBO_COUNT = 100;
const COUNT_INTERVAL = 10;
const TURBO_INTERVAL = 1;
const DEBOUNCE_INTERVAL = 150;

const GlobalPendingProgress = () => {
    const location = useNonBlockingLocation();
    const pathname = location.pathname;
    const isPendingInternal = useGlobalTransition(state => state.isPending);
    const isPending = useDebounce(isPendingInternal, DEBOUNCE_INTERVAL);
    const [count, setCount] = useState(0);
    const [key, setKey] = useState(0);
    const [isDelaying, setIsDelaying] = useState(false);
    const timeout = useRef<NodeJS.Timeout>();

    const reset = useCallback(() => {
        setCount(0);
        setKey(key => key + 1);
    }, []);

    useEffect(() => {
        reset();
    }, [pathname, reset]);

    useEffect(() => {
        let intervalId: NodeJS.Timeout | undefined;

        const increment = (interval: number, max: number) =>
            setInterval(() => {
                if (count < max) {
                    setCount(prevCount => prevCount + 1);
                } else {
                    clearInterval(intervalId);
                }
            }, interval);

        if (isPending) {
            clearTimeout(timeout.current);
            setIsDelaying(true);
            timeout.current = setTimeout(() => setIsDelaying(false), 1000);
            intervalId = increment(COUNT_INTERVAL, MAX_COUNT);
        } else if (isDelaying) {
            intervalId = increment(TURBO_INTERVAL, TURBO_COUNT);
        } else {
            reset();
        }

        return () => {
            clearInterval(intervalId);
        };
    }, [count, isPending, isDelaying, reset]);

    return (
        <>
            <Progress.Root
                key={key}
                value={count}
                className={twMerge('z-outermost absolute left-0 right-0 top-0 h-1')}
                aria-label={'Global progress bar'}
            >
                <Progress.Indicator
                    className={twMerge('bg-warning-400 h-1')}
                    style={{
                        width: `${count}%`,
                        opacity: count > MAX_COUNT ? `${(100 - count) / 100}` : '1',
                        transition: 'width 350ms ease-out, opacity 250ms 250ms ease-in',
                        transform: 'translate3d(0,0,0)',
                    }}
                />
            </Progress.Root>
            <GlobalLoader />
        </>
    );
};

const GlobalLoader = () => {
    const isFetching = useIsFetching();

    if (isFetching === 0) {
        return null;
    }

    return (
        <span data-testid={TestIds.Global.RawLoader} className={'sr-only'}>
            Loading...
        </span>
    );
};

export { GlobalPendingProgress };
